<template>
  <section class="section section--soft" id="paketler">
    <div class="container">
      <p class="eyebrow">{{ t("packages.eyebrow") }}</p>
      <h2 class="section-title">{{ t("packages.title") }}</h2>
      <p class="section-lead">{{ t("packages.lead") }}</p>

      <div class="tabs">
        <button
          v-for="group in packageGroups"
          :key="group.id"
          type="button"
          :class="{ 'is-on': active === group.id }"
          @click="active = group.id"
        >
          {{ t(`packages.groups.${group.id}`) }}
        </button>
      </div>

      <div
        class="pkg-grid"
        :class="{
          'pkg-grid--one': current.items.length === 1,
          'pkg-grid--two': current.items.length === 2,
        }"
      >
        <article
          v-for="item in current.items"
          :key="item.id"
          class="pkg-card"
          :class="{ 'is-popular': item.popular }"
        >
          <span v-if="item.popular" class="badge">{{ t("packages.popular") }}</span>
          <div v-if="t(`packages.items.${item.id}.tier`)" class="tier">
            {{ t(`packages.items.${item.id}.tier`) }}
          </div>
          <h4>{{ t(`packages.items.${item.id}.name`) }}</h4>
          <p class="summary">{{ t(`packages.items.${item.id}.summary`) }}</p>
          <div class="price">
            <small>{{ t("packages.priceLabel") }}</small>
            <div class="price__row">
              <del v-if="item.oldPrice" class="price__old">{{ displayPrice(item.oldPrice) }}</del>
              <strong>{{ displayPrice(item.price) }}</strong>
              <span v-if="item.oldPrice" class="price__discount">
                {{ displayDiscount(item) }}
              </span>
            </div>
          </div>
          <ul>
            <li v-for="(feature, index) in featureList(item.id)" :key="index">
              <svg class="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
                <path d="M20 6 9 17l-5-5" />
              </svg>
              <span>{{ feature }}</span>
            </li>
          </ul>
          <a
            class="btn btn--primary btn--block"
            :href="whatsappHref(t(`packages.wa.${item.id}`))"
            target="_blank"
            rel="noopener"
          >
            {{ t("packages.cta") }}
          </a>
          <p class="note">
            {{ t(`packages.notes.${current.note}`) }}
            · {{ t("packages.payHint") }}
          </p>
        </article>
      </div>

      <div class="pkg-compare">
        <button class="btn btn--ghost pkg-compare__btn" type="button" @click="compareOpen = !compareOpen">
          {{ compareOpen ? t("packages.compare.close") : t("packages.compare.open") }}
        </button>
        <div v-if="compareOpen" class="pkg-compare__wrap">
          <table>
            <thead>
              <tr>
                <th>{{ t("packages.compare.col") }}</th>
                <th v-for="item in current.items" :key="item.id">
                  {{ t(`packages.items.${item.id}.tier`) || t(`packages.items.${item.id}.name`) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in compareRows" :key="index">
                <th>{{ row.label }}</th>
                <td v-for="(cell, i) in row.cells" :key="i">
                  <span v-if="cell === true" class="pkg-tick" aria-label="Var">✓</span>
                  <span v-else-if="cell === false" class="pkg-dash" aria-label="Yok">—</span>
                  <span v-else>{{ cell }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { whatsappHref } from "@/data/company";
import {
  formatPrice,
  formatDiscountBadge,
  packageGroups,
} from "@/data/packages";

export default {
  name: "PackagesSection",
  setup() {
    const { t, tm, locale } = useI18n();
    const active = ref("corporate");
    const compareOpen = ref(false);
    const current = computed(
      () => packageGroups.find((group) => group.id === active.value) || packageGroups[0]
    );
    const compareRows = computed(() => {
      const rows = tm(`packages.compare.${active.value}`);
      return Array.isArray(rows) ? rows : [];
    });
    function featureList(id) {
      const list = [];
      for (let i = 0; i < 24; i += 1) {
        const key = `packages.items.${id}.features.${i}`;
        const value = t(key);
        if (value === key) break;
        list.push(value);
      }
      return list;
    }
    function displayPrice(val) {
      return formatPrice(val, locale.value);
    }
    function displayDiscount(item) {
      return formatDiscountBadge(item.price, item.oldPrice, locale.value);
    }
    return {
      t,
      locale,
      packageGroups,
      displayPrice,
      displayDiscount,
      whatsappHref,
      featureList,
      active,
      current,
      compareOpen,
      compareRows,
    };
  },
};
</script>
